/// <amd-module name="TFS/Samples/PageBuilder/PageBuilderView" />
import * as React from "react";

import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Accordion, IAccordionItem } from "VSSUI/DeprecatedAccordion";
import { FilterBar } from "VSSUI/FilterBar";
import { Header, TitleSize } from "VSSUI/Header";
import { HeaderCommandBar, HeaderCommandBarWithFilter, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IIconProps } from "VSSUI/Icon";
import { Page } from "VSSUI/Page";
import { IPickListSelection, PickListDropdown } from "VSSUI/PickList";
import { Splitter, SplitterElementPosition } from "VSSUI/Splitter";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { TextField } from "VSSUI/TextField";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, IFilter } from "VSSUI/Utilities/Filter";
import { VssDetailsList } from "VSSUI/VssDetailsList";

interface ICommandsSectionProps {
    headerCommandItems: IHeaderCommandBarItem[];
    allowPrimary?: boolean;
    allowSplit?: boolean;
    onItemsChanged: () => void;
    subtle?: boolean;
}

class CommandsSection extends React.Component<ICommandsSectionProps> {
    public render() {
        const items = this.props.headerCommandItems.map((commandItem, index) => {
            return (
                <CommandSection
                    allowPrimary={this.props.allowPrimary}
                    allowSplit={this.props.allowSplit}
                    key={index}
                    headerCommandItem={commandItem}
                    onItemChanged={this.onItemChanged}
                    onItemRemoved={this.onItemRemoved}
                />
            );
        });

        return (
            <div className="flex-column">
                <div className="page-builder-option">Commands</div>
                {items}
                <Button className="page-builder-option" onClick={this.onAddClick}>
                    Add
                </Button>
            </div>
        );
    }

    private onItemChanged = () => {
        this.props.onItemsChanged();
    };

    private onItemRemoved = (headerCommandItem: IHeaderCommandBarItem) => {
        const index = this.props.headerCommandItems.indexOf(headerCommandItem);
        this.props.headerCommandItems.splice(index, 1);
        this.props.onItemsChanged();
    };

    private onAddClick = () => {
        this.props.headerCommandItems.push({
            iconProps: {
                iconName: "Save"
            },
            subtle: this.props.subtle,
            id: "save",
            text: "Save"
        });

        this.props.onItemsChanged();
    };
}

interface ICommandSectionProps {
    headerCommandItem: IHeaderCommandBarItem;
    allowPrimary?: boolean;
    allowSplit?: boolean;
    onItemChanged: () => void;
    onItemRemoved: (headerCommandItem: IHeaderCommandBarItem) => void;
}

class CommandSection extends React.Component<ICommandSectionProps> {
    public render() {
        return (
            <div className="flex-column command">
                <TextField
                    className="page-builder-option"
                    placeholder="Name"
                    value={this.props.headerCommandItem.text}
                    onChange={this.onNameChanged}
                />
                <TextField
                    className="page-builder-option"
                    placeholder="Icon"
                    value={this.props.headerCommandItem.iconProps && this.props.headerCommandItem.iconProps.iconName}
                    onChange={this.onIconChanged}
                />
                {this.props.allowPrimary && (
                    <Checkbox
                        className="flex-self-start page-builder-option"
                        label="Primary"
                        checked={!!this.props.headerCommandItem.isPrimary}
                        onChange={this.onPrimaryChange}
                    />
                )}
                {this.props.allowSplit && (
                    <Checkbox
                        className="flex-self-start page-builder-option"
                        label="Split"
                        checked={!!this.props.headerCommandItem.subMenuProps}
                        onChange={this.onSplitChange}
                    />
                )}
                <Button className="page-builder-option" onClick={this.onRemoveClick}>
                    Remove
                </Button>
            </div>
        );
    }

    private onNameChanged = (e: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        this.props.headerCommandItem.text = newValue === "" ? undefined : newValue;
        this.props.onItemChanged();
    };

    private onIconChanged = (e: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        if (newValue) {
            this.props.headerCommandItem.iconProps = {
                iconName: newValue
            };
        } else {
            this.props.headerCommandItem.iconProps = undefined;
        }
        this.props.onItemChanged();
    };

    private onPrimaryChange = (ev: React.FormEvent<HTMLElement | HTMLInputElement>, checked: boolean) => {
        this.props.headerCommandItem.isPrimary = checked;
        this.props.onItemChanged();
    };

    private onSplitChange = (ev: React.FormEvent<HTMLElement | HTMLInputElement>, checked: boolean) => {
        if (checked) {
            this.props.headerCommandItem.subMenuProps = {
                id: "split",
                items: new ObservableArray([{ text: "Item 1", id: "key2" }, { text: "Item 2", id: "key2" }])
            };
        } else {
            this.props.headerCommandItem.subMenuProps = undefined;
        }
        this.props.onItemChanged();
    };

    private onRemoveClick = () => {
        this.props.onItemRemoved(this.props.headerCommandItem);
    };
}

interface IExampleItem {
    name: string;
}

interface IPageBuilderState {
    headerCommandBarItems: IHeaderCommandBarItem[];
    filterIsInline?: boolean;
    showCustomSubheader?: boolean;
    tabBarViewActions: IHeaderCommandBarItem[];
    tabSize?: TabSize;
    title?: string;
    titleIconName?: string;
    titleSize?: TitleSize;
}

class PageBuilder extends VssComponent<{}, IPageBuilderState> {
    private filter: IFilter = new Filter();
    private items: IExampleItem[] = [];
    private showFilter: ObservableValue<boolean> = new ObservableValue<boolean>(false);
    private selectedTabId = new ObservableValue<string>("tab1");

    constructor(props: {}) {
        super(props);

        for (let i = 0; i < 100; i++) {
            this.items.push({ name: "Item" + i });
        }

        this.state = {
            titleSize: TitleSize.Large,
            title: "Sample header",
            tabSize: TabSize.Tall,
            filterIsInline: true,
            headerCommandBarItems: [
                {
                    text: "Create project",
                    id: "create-project",
                    iconProps: {
                        iconName: "Add"
                    },
                    isPrimary: true
                }
            ],
            tabBarViewActions: [
                {
                    subtle: true,
                    id: "view-options",
                    iconProps: {
                        iconName: "Equalizer"
                    }
                }
            ]
        };

        this.showFilter.value = true;
    }

    public render() {
        return (
            <Splitter
                className="scroll-hidden"
                fixedElement={SplitterElementPosition.Near}
                initialFixedSize={300}
                onRenderNearElement={this.onRenderNearElement}
                onRenderFarElement={this.onRenderFarElement}
                farElementClassName="flex-column"
                nearElementClassName="page-builder v-scroll-auto"
            />
        );
    }

    private onRenderNearElement = () => {
        const items: IAccordionItem[] = [
            {
                key: "header",
                title: "Header",
                onRenderContent: this.renderHeaderSection
            },
            {
                key: "tabs",
                title: "Tabs",
                onRenderContent: this.renderTabsSection
            },
            {
                key: "filter-bar",
                title: "Filter Bar",
                onRenderContent: this.renderFilterBarSection
            }
        ];
        return <Accordion items={items} initiallyExpandAll={true} />;
    };

    private onRenderFarElement = () => {
        const titleIconProps: IIconProps | undefined = this.state.titleIconName
            ? {
                  iconName: this.state.titleIconName
              }
            : undefined;
        return (
            <Page className="v-scroll-auto">
                <Header
                    titleSize={this.state.titleSize}
                    titleIconProps={titleIconProps}
                    title={this.state.title}
                    className="flex-noshrink"
                    commandBarItems={this.state.headerCommandBarItems}
                />
                {this.state.showCustomSubheader && (
                    <div className="page-content flex-row flex-noshrink subheader">
                        <div className="text body-l">CI build of master </div>
                        <div className="flex-row body-s fontWeightSemiBold">
                            <div className="tag">Self-host</div>
                            <div className="tag">Another-tag</div>
                        </div>
                    </div>
                )}
                <TabBar
                    tabSize={this.state.tabSize}
                    renderAdditionalContent={this.renderAdditionalContent}
                    selectedTabId={this.selectedTabId}
                    onSelectedTabChanged={this.onSelectedTabChanged}
                >
                    <Tab name="Tab 1" id="tab1" />
                    <Tab name="Tab 2" id="tab2" />
                </TabBar>
                {!this.state.filterIsInline && (
                    <ConditionalChildren renderChildren={this.showFilter}>{this.renderFilterBar("flex-noshrink")}</ConditionalChildren>
                )}
                <div className="page-content">
                    <VssDetailsList items={this.items} />
                </div>
            </Page>
        );
    };

    private onSelectedTabChanged = (newTabId: string) => {
        this.selectedTabId.value = newTabId;
    };

    private renderAdditionalContent = () => {
        const menuItems = [...this.state.tabBarViewActions];
        const filterBar = this.state.filterIsInline ? this.renderFilterBar() : null;
        let headerCommandBar: JSX.Element | undefined = undefined;
        if (!this.state.filterIsInline) {
            headerCommandBar = (
                <HeaderCommandBarWithFilter className="flex-self-stretch" items={menuItems} filter={this.filter} filterToggled={this.showFilter} />
            );
        } else if (menuItems.length > 0) {
            headerCommandBar = <HeaderCommandBar className="flex-self-stretch" items={menuItems} />;
        }
        return (
            <div className="flex-row">
                {headerCommandBar}
                {filterBar}
            </div>
        );
    };

    private renderFilterBar = (className?: string) => {
        return (
            <FilterBar className={className} filter={this.filter} onDismissClicked={this.onToggleFilterClick}>
                <KeywordFilterBarItem filterItemKey="keyword" />
            </FilterBar>
        );
    };

    private onToggleFilterClick = () => {
        this.showFilter.value = !this.showFilter.value;
    };

    private renderHeaderSection = () => {
        return (
            <div>
                <PickListDropdown
                    className="page-builder-option"
                    getPickListItems={this.getTitleSizePickListItems}
                    selectedItems={[this.state.titleSize === TitleSize.Medium ? "Medium" : "Large"]}
                    onSelectionChanged={this.onSelectedTitleSizeChanged}
                />
                <TextField className="page-builder-option" placeholder="Title" value={this.state.title} onChange={this.onTitleTextChanged} />
                <TextField
                    className="page-builder-option"
                    placeholder="Icon"
                    value={this.state.titleIconName}
                    onChange={this.onTitleIconNameChanged}
                />
                <Checkbox
                    className="page-builder-option"
                    label="Custom sub-header"
                    checked={!!this.state.showCustomSubheader}
                    onChange={this.onCustomSubHeaderChanged}
                />
                <CommandsSection
                    allowPrimary={true}
                    allowSplit={true}
                    headerCommandItems={this.state.headerCommandBarItems}
                    onItemsChanged={this.onItemsChanged}
                />
            </div>
        );
    };

    private renderTabsSection = () => {
        return (
            <div>
                <PickListDropdown
                    className="page-builder-option"
                    getPickListItems={this.getTabSizePickListItems}
                    selectedItems={[this.state.tabSize]}
                    onSelectionChanged={this.onSelectedTabSizeChanged}
                />
                <CommandsSection headerCommandItems={this.state.tabBarViewActions} onItemsChanged={this.onItemsChanged} subtle={true} />
            </div>
        );
    };

    private renderFilterBarSection = () => {
        return (
            <div className="page-builder-option">
                <Checkbox label="In-line" checked={!!this.state.filterIsInline} onChange={this.onFilterInlineChange} />
            </div>
        );
    };

    private onItemsChanged = () => {
        this.forceUpdate();
    };

    private getTitleSizePickListItems = () => {
        return ["Medium", "Large"];
    };

    private onSelectedTitleSizeChanged = (selection: IPickListSelection) => {
        this.setState({
            titleSize: selection.selectedItems[0] === "Medium" ? TitleSize.Medium : TitleSize.Large
        });
    };

    private onTitleTextChanged = (e: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        this.setState({
            title: newValue
        });
    };

    private onTitleIconNameChanged = (e: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        this.setState({
            titleIconName: newValue === "" ? undefined : newValue
        });
    };

    private onCustomSubHeaderChanged = (ev: React.FormEvent<HTMLElement | HTMLInputElement>, checked: boolean) => {
        this.setState({ showCustomSubheader: checked });
    };

    private getTabSizePickListItems = () => {
        return [TabSize.Compact, TabSize.LargeLink, TabSize.Tall];
    };

    private onSelectedTabSizeChanged = (selection: IPickListSelection) => {
        this.setState({
            tabSize: selection.selectedItems[0]
        });
    };

    private onFilterInlineChange = (ev: React.FormEvent<HTMLElement | HTMLInputElement>, checked: boolean) => {
        this.showFilter.value = true;
        this.setState({ filterIsInline: checked });
    };
}

Components.add("page-builder-view", PageBuilder);
